import * as antd from 'antd';
import {ConfigProvider} from 'antd';
import locale from 'antd/lib/locale/en_GB';
import {AnchorLink, Spinner} from 'components';
import {EnableSmsMFA} from 'components/EnableSmsMFA';
import {ErrorView} from 'components/ErrorView';
import {Route} from 'components/Route';
import {getIn} from 'formik';
import {GlobalStyle} from 'global.styles';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {usePolicyToUpdate} from 'hooks/usePolicyToUpdate';
import React, {Suspense, useEffect} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {Redirect, Switch, useLocation} from 'react-router-dom';
import {Account} from 'routes/Account';
import {Document} from 'routes/Account/Document/Document';
import {EditSubscription} from 'routes/Account/EditSubscription';
import {PayByLink} from 'routes/Account/PayByLink/PayByLink';
import {Provider} from 'routes/Account/Provider';
import {EditProvider} from 'routes/Account/Provider/EditProvider';
import {Subscription} from 'routes/Account/Subscription';
import {Accounts} from 'routes/Accounts';
import {ApiExplorer} from 'routes/ApiExplorer';
import {Dashboard} from 'routes/Dashboard';
import {PaymentEvents} from 'routes/Events';
import {MasterAccount} from 'routes/MasterAccount';
import {MasterAccounts} from 'routes/MasterAccounts';
import {CreateMasterAccount} from 'routes/MasterAccounts/CreateMasterAccount';
import {NotFound} from 'routes/NotFound';
import {Payment} from 'routes/Payment';
import {Payments} from 'routes/Payments';
import {PolicyUpdate} from 'routes/PolicyUpdate';
import {Settings} from 'routes/Settings';
import {User} from 'routes/User';
import {Users} from 'routes/Users';
import {config} from 'stage.config';
import styled from 'styled-components';
import {MainHeader} from './MainHeader';
import { Affiliates } from 'routes/Affiliates/Affiliates';
import { Affiliate } from 'routes/Affiliate/Affiliate';

const Layout = styled(antd.Layout)`
  && {
    min-height: 100%;
  }
`;

const Wrapper = styled(antd.Layout.Content)`
  margin-top: 75px;
  margin-bottom: 50px;
  padding: 0 20px;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Footer = styled(antd.Layout.Footer)`
  text-align: center;
`;

const MainLayout: React.FC<{}> = () => {
  const location = useLocation();
  const {isMaster, isAdmin} = useAccessLevel();
  const isPolicyUpdateRequired = usePolicyToUpdate();

  // restore scroll position when location changes
  useEffect(() => {
    const preserveScroll = getIn(location, 'state.preserveScroll', false);
    if (!preserveScroll) {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <ConfigProvider locale={locale}>
      <Layout>
        <GlobalStyle />
        <MainHeader minimal={false} />
        <Wrapper>
          <Content>
            <ErrorBoundary FallbackComponent={ErrorView}>
              <Suspense fallback={<Spinner />}>
                {isPolicyUpdateRequired ? (
                  <Switch>
                    <Route path="/policy-update" component={PolicyUpdate} />
                    <Redirect to="/policy-update" />
                  </Switch>
                ) : (
                  <Switch>
                    <Route path="/" exact component={Dashboard} />
                    {isAdmin && <Route path="/users/:userId" component={User} />}
                    {isAdmin && <Route path="/users" component={Users} />}
                    {isMaster && <Route path="/payments/new" component={PayByLink} />}
                    {isMaster && <Route path="/payments/:chargeId" component={Payment} />}
                    {isMaster && <Route path="/payments" component={Payments} />}
                    {isMaster && <Route path="/payment-events" component={PaymentEvents} />}
                    {isAdmin && (
                      <Route path="/accounts/:accountId/providers/new" component={EditProvider} />
                    )}
                    {isAdmin && (
                      <Route
                        path="/accounts/:accountId/providers/:providerId/edit"
                        component={EditProvider}
                      />
                    )}
                    {isAdmin && (
                      <Route path="/master-accounts/new" component={CreateMasterAccount} />
                    )}
                    {isAdmin && (
                      <Route path="/master-accounts/:masterAccountId" component={MasterAccount} />
                    )}
                    {isAdmin && <Route path="/master-accounts" component={MasterAccounts} />}
                    {isAdmin && (
                      <Route
                        path="/accounts/:accountId/providers/:providerId"
                        component={Provider}
                      />
                    )}
                    {isAdmin && (
                      <Route
                        path="/accounts/:accountId/documents/:documentId"
                        component={Document}
                      />
                    )}
                    <Route
                      path="/accounts/:accountId/subscriptions/new"
                      component={EditSubscription}
                    />
                    <Route
                      path="/accounts/:accountId/subscriptions/:subscriptionId/edit"
                      component={EditSubscription}
                    />
                    <Route
                      path="/accounts/:accountId/subscriptions/:subscriptionId"
                      component={Subscription}
                    />
                    <Route path="/accounts/:accountId" component={Account} />
                    <Route path="/accounts" component={Accounts} />
                    {isAdmin && <Route path="/affiliates/:affiliateId" component={Affiliate} />}
                    {isAdmin && <Route path="/affiliates" component={Affiliates} />}
                    {isMaster && <Route path="/api-explorer" component={ApiExplorer} />}
                    {isMaster && <Route path="/settings" component={Settings} />}
                    <Redirect from="/policy-update" to="/" />
                    <Route component={NotFound} />
                  </Switch>
                )}
                {config.production && <EnableSmsMFA headless />}
              </Suspense>
            </ErrorBoundary>
          </Content>
        </Wrapper>
        <Footer>
          &copy;{' '}
          <AnchorLink href="https://monei.com" external>
            MONEI Inc.
          </AnchorLink>
        </Footer>
      </Layout>
    </ConfigProvider>
  );
};

export default MainLayout;
